/**
 * Single-routine activity pages for AHC and APD — header + run history.
 * (CDID has its own page with an Activity/Tickets tab split: CdidPage.tsx.)
 */
import type { AutobuildKind } from '@a7y/types';
import { cn } from '@/lib/utils';
import { ApdTicketsTab } from './ApdTicketsTab';
import { PageHeader } from '@/components/PageHeader';
import { RunHistory } from './RunHistory';
import { useHashTab, useScheduleStatus } from './shared';

function RoutineActivityPage({
  kind,
  title,
  description,
}: {
  kind: AutobuildKind;
  title: string;
  /** Sentence before the live "Next run in h:mm" tail. */
  description: string;
}) {
  const schedule = useScheduleStatus(kind);
  return (
    <div className="space-y-4">
      <PageHeader title={title} subtitle={`${description} ${schedule}`} />
      <RunHistory kind={kind} />
    </div>
  );
}

export function AhcPage() {
  return (
    <RoutineActivityPage
      kind="ahc"
      title="Automated Health Check (AHC)"
      description="Every AHC run and its outcome — the nightly BigQuery error sweep that triages findings and files or fixes the issues it can. Runs daily at 23:00 UTC."
    />
  );
}

type ApdTabId = 'activity' | 'tickets';

const APD_TABS: { id: ApdTabId; label: string }[] = [
  { id: 'activity', label: 'Activity' },
  { id: 'tickets', label: 'Tickets' },
];
/** Derived from APD_TABS so the hash vocabulary can never drift from the tabs. */
const APD_TAB_IDS: ApdTabId[] = APD_TABS.map((t) => t.id);

export function ApdPage() {
  const [activeTab, setActiveTab] = useHashTab(APD_TAB_IDS);
  const schedule = useScheduleStatus('apd');

  return (
    <div className="space-y-4">
      <PageHeader
        title="APD: Automated Prod Deploy"
        subtitle={`Activity: every deploy session and its outcome. Tickets: what each deploy carried to production. Runs from immutable green staging candidates. ${schedule}`}
      />

      <div className="border-b border-zinc-200 dark:border-zinc-700">
        <nav className="-mb-px flex gap-6">
          {APD_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'border-b-2 pb-3 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'activity' && <RunHistory kind="apd" />}
      {activeTab === 'tickets' && <ApdTicketsTab />}
    </div>
  );
}
